import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineSeparator,
} from "@mui/lab";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EventIcon from "@mui/icons-material/Event";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import Fab from "@mui/material/Fab";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import PageBanner from "../../components/PageBanner";
import PageContent from "../../components/PageContent";
import { AppConfirmDialog, AppToast } from "../../components/AppFeedback";
import DatagridNavLink from "../../components/DatagridNavLink";
import { fetchClientDropdown } from "../../services/clientService";
import { fetchProjectTimelines, fetchProjectsDropdownByClient } from "../../services/projectService";
import {
  createPatient,
  deletePatient,
  deletePatientDocument,
  fetchPatientDatagridRows,
  fetchPatientDocuments,
  fetchPatientSchedules,
  generatePatientPid,
  getPatientDocumentDownloadUrl,
  updatePatient,
  updatePatientScheduleDate,
  uploadPatientDocument,
} from "../../services/patientService";
import { useUserContext } from "../../context/UserContext";
import { COUNTRIES } from "../../constants/countries";
import { formatDate } from "../../utils/dateFormat";
import PatientSchedulePanel, {
  mapPatientScheduleRows,
  ScheduleDateDialog,
  emptyScheduleDialogState,
} from "./PatientSchedulePanel";
import { buildScheduleDialogState } from "./patientScheduleUtils";
import {
  clearPatientFieldError,
  formatPatientValidationMessage,
  validatePatientForm,
} from "./patientFormUtils";

function toErrorMessage(error, fallback) {
  const raw = error?.message || "";
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.detail === "string") return parsed.detail;
  } catch {
    // ignore
  }
  return String(raw || fallback);
}

function addDays(dateStr, days) {
  const dt = new Date(dateStr);
  if (Number.isNaN(dt.getTime())) return "";
  dt.setDate(dt.getDate() + Number(days || 0));
  return dt.toISOString().slice(0, 10);
}

const initialForm = {
  pid: "",
  first_name: "",
  middle_name: "",
  last_name: "",
  gender: "",
  date_of_birth: "",
  enrollment_date: "",
  email: "",
  phone_local: "",
  address1: "",
  address2: "",
  country: "United States",
  city: "",
  state: "",
  zipcode: "",
  client_id: "",
  project_id: "",
  status: "active",
};

const modalSelectSx = { width: { xs: "100%", md: 260 } };
const doctypeOptions = [
  { value: "clinical", label: "Clinical" },
  { value: "consent", label: "Consent" },
];

const PatientList = () => {
  const navigate = useNavigate();
  const { roles = [] } = useUserContext();
  const canManage = roles.some((role) => {
    const upper = String(role).toUpperCase();
    return upper === "ADMIN" || upper === "MANAGER";
  });

  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: "created_at", sort: "desc" }]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [clientFilter, setClientFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [enrollmentDateFrom, setEnrollmentDateFrom] = useState("");
  const [enrollmentDateTo, setEnrollmentDateTo] = useState("");

  const [clients, setClients] = useState([]);
  const [projectsByClient, setProjectsByClient] = useState([]);

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [schedulePreview, setSchedulePreview] = useState([]);

  const [editId, setEditId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docPatient, setDocPatient] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ doctype: "clinical", document_name: "", file: null });
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const selectedCountry = useMemo(
    () => COUNTRIES.find((country) => country.name === form.country),
    [form.country]
  );

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [expandedPatientId, setExpandedPatientId] = useState(null);
  const [scheduleCache, setScheduleCache] = useState({});
  const [schedulePatientId, setSchedulePatientId] = useState(null);
  const [scheduleDialog, setScheduleDialog] = useState(emptyScheduleDialogState);
  const [scheduling, setScheduling] = useState(false);

  const DATA_GRID_COLUMN_COUNT = 13;

  useEffect(() => {
    setExpandedPatientId(null);
  }, [paginationModel, sortModel, searchTerm, clientFilter, projectFilter, enrollmentDateFrom, enrollmentDateTo]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let cancelled = false;
    const loadClients = async () => {
      try {
        const items = await fetchClientDropdown();
        if (!cancelled) setClients(items || []);
      } catch {
        if (!cancelled) setClients([]);
      }
    };
    loadClients();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      setError("");
      try {
        const sort = sortModel[0] || {};
        const data = await fetchPatientDatagridRows({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          search: searchTerm,
          clientId: clientFilter,
          projectId: projectFilter,
          enrollmentDateFrom,
          enrollmentDateTo,
          sortField: sort.field || "created_at",
          sortOrder: sort.sort || "desc",
        });
        if (cancelled) return;
        setRows(data.rows || []);
        setRowCount(data.rowCount || 0);
      } catch (e) {
        if (!cancelled) setError(toErrorMessage(e, "Failed to fetch subjects"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [paginationModel, sortModel, searchTerm, clientFilter, projectFilter, enrollmentDateFrom, enrollmentDateTo]);

  const loadProjectsForClient = async (cid, forForm = false) => {
    if (!cid) {
      setProjectsByClient([]);
      return;
    }
    const items = await fetchProjectsDropdownByClient(cid);
    setProjectsByClient(items || []);
    if (!forForm && projectFilter && !(items || []).some((p) => String(p.id) === String(projectFilter))) {
      setProjectFilter("");
    }
  };

  const loadSchedulePreview = async (projectId, anchorDateStr) => {
    if (!projectId) {
      setSchedulePreview([]);
      return;
    }
    const timelines = await fetchProjectTimelines(projectId);
    const sorted = [...(timelines || [])].sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
    const anchor =
      anchorDateStr && String(anchorDateStr).trim()
        ? String(anchorDateStr).trim().slice(0, 10)
        : new Date().toISOString().slice(0, 10);
    let previousOptimal = anchor;
    const preview = sorted.map((row, index) => {
      const type = String(row.type || "").toLowerCase();
      const tolerance = Number(row.tolerance || 0);
      const offset = Number(row.offset || 0);
      const optimal =
        index === 0
          ? anchor
          : type === "static"
            ? row.date || previousOptimal
            : addDays(previousOptimal, offset >= 1 ? offset : 1);
      const windowOpen = addDays(optimal, -Math.max(tolerance, 0));
      const windowClose = addDays(optimal, Math.max(tolerance, 0));
      previousOptimal = optimal;
      return {
        id: row.id || row.position,
        schedule: row.position,
        type,
        tolerance,
        optimal_date: optimal,
        window_open: windowOpen,
        window_close: windowClose,
      };
    });
    setSchedulePreview(preview);
  };

  useEffect(() => {
    if (!createOpen || editId) return;
    if (!form.project_id) {
      setSchedulePreview([]);
      return;
    }
    loadSchedulePreview(form.project_id, form.enrollment_date);
  }, [createOpen, form.project_id, form.enrollment_date, editId]);

  const openCreate = async (row = null) => {
    setCreateOpen(true);
    setEditId(row?.id || null);
    setFieldErrors({});
    if (!row) {
      setForm(initialForm);
      setProjectsByClient([]);
      setSchedulePreview([]);
      return;
    }
    const countryName = row.country || "United States";
    const countryConfig = COUNTRIES.find((country) => country.name === countryName);
    let phoneLocal = row.phone || "";
    if (countryConfig?.dialCode && phoneLocal.startsWith(countryConfig.dialCode)) {
      phoneLocal = phoneLocal.slice(countryConfig.dialCode.length);
    }
    const next = {
      pid: row.pid || "",
      first_name: row.first_name || "",
      middle_name: row.middle_name || "",
      last_name: row.last_name || "",
      gender: row.gender || "",
      date_of_birth: row.date_of_birth || "",
      enrollment_date: row.enrollment_date || "",
      email: row.email || "",
      phone_local: phoneLocal,
      address1: row.address1 || "",
      address2: row.address2 || "",
      country: countryName,
      city: row.city || "",
      state: row.state || "",
      zipcode: row.zipcode || "",
      client_id: row.client_id || "",
      project_id: row.project_id || "",
      status: String(row.status || "").toLowerCase() === "inactive" ? "inactive" : "active",
    };
    setForm(next);
    if (next.client_id) {
      await loadProjectsForClient(next.client_id, true);
    }
    if (next.project_id) {
      try {
        const stored = await fetchPatientSchedules(row.id);
        if (Array.isArray(stored) && stored.length > 0) {
          setSchedulePreview(
            stored.map((s) => ({
              id: s.id,
              schedule: s.order,
              type: s.schedule_type,
              tolerance: Number((s.window_size || "0").split(" ")[0] || 0),
              optimal_date: s.optimal_date,
              window_open: s.window_open_date,
              window_close: s.window_close_date,
            }))
          );
        } else {
          await loadSchedulePreview(next.project_id, next.enrollment_date);
        }
      } catch {
        await loadSchedulePreview(next.project_id, next.enrollment_date);
      }
    }
  };

  const handleClientChange = async (cid) => {
    setForm((prev) => ({ ...prev, client_id: cid, project_id: "", pid: "", enrollment_date: "" }));
    await loadProjectsForClient(cid, true);
    setSchedulePreview([]);
  };

  const handleProjectChange = async (projectId) => {
    setForm((prev) => ({ ...prev, project_id: projectId, pid: "" }));
    if (!form.client_id || !projectId) return;
    try {
      const pidData = await generatePatientPid({ client_id: form.client_id, project_id: projectId });
      setForm((prev) => ({ ...prev, pid: pidData.pid || "" }));
    } catch {
      setForm((prev) => ({ ...prev, pid: "" }));
    }
  };

  const handleSave = async () => {
    const phoneFull = `${selectedCountry?.dialCode || ""}${(form.phone_local || "").trim()}`;
    const validation = validatePatientForm(form, phoneFull, {
      requireEnrollmentDate: !editId,
    });
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setSnackbar({
        open: true,
        message: formatPatientValidationMessage(validation.missingLabels),
        severity: "warning",
      });
      return;
    }
    setFieldErrors({});

    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim() || null,
        last_name: form.last_name.trim(),
        gender: form.gender,
        date_of_birth: form.date_of_birth || null,
        phone: phoneFull || null,
        address1: form.address1.trim() || null,
        address2: form.address2.trim() || null,
        country: form.country.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zipcode: form.zipcode.trim() || null,
        client_id: form.client_id,
        project_id: Number(form.project_id),
        status: form.status || "active",
      };
      if (!editId) {
        payload.email = form.email.trim() || null;
        payload.enrollment_date = form.enrollment_date || null;
      }
      if (editId) {
        await updatePatient(editId, payload);
        setSnackbar({ open: true, message: "Subject updated", severity: "success" });
      } else {
        await createPatient(payload);
        setSnackbar({
          open: true,
          message: "Subject created. PATIENT user is provisioned and credentials email is sent if newly created.",
          severity: "success",
        });
      }
      setCreateOpen(false);
      setPaginationModel((prev) => ({ ...prev }));
      setSearchTerm((prev) => prev);
      const sort = sortModel[0] || {};
      const data = await fetchPatientDatagridRows({
        page: paginationModel.page,
        pageSize: paginationModel.pageSize,
        search: searchTerm,
        clientId: clientFilter,
        projectId: projectFilter,
        enrollmentDateFrom,
        enrollmentDateTo,
        sortField: sort.field || "created_at",
        sortOrder: sort.sort || "desc",
      });
      setRows(data.rows || []);
      setRowCount(data.rowCount || 0);
    } catch (e) {
      setSnackbar({ open: true, message: toErrorMessage(e, "Failed to save subject"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deletePatient(deleteId);
      setSnackbar({ open: true, message: "Subject deleted", severity: "success" });
      setDeleteId(null);
      setRows((prev) => prev.filter((r) => r.id !== deleteId));
      setRowCount((prev) => Math.max(prev - 1, 0));
    } catch (e) {
      setSnackbar({ open: true, message: toErrorMessage(e, "Failed to delete subject"), severity: "error" });
      setDeleteId(null);
    }
  };

  const openDocumentDialog = async (row) => {
    setDocPatient(row);
    setDocDialogOpen(true);
    setUploadForm({ doctype: "clinical", document_name: "", file: null });
    try {
      const docs = await fetchPatientDocuments(row.id);
      setDocuments(docs || []);
    } catch (e) {
      setDocuments([]);
      setSnackbar({ open: true, message: toErrorMessage(e, "Failed to load documents"), severity: "error" });
    }
  };

  const handleUploadDocument = async () => {
    if (!docPatient?.id || !uploadForm.file || !uploadForm.document_name.trim()) {
      setSnackbar({ open: true, message: "Document name and file are required", severity: "warning" });
      return;
    }
    setUploading(true);
    try {
      await uploadPatientDocument(docPatient.id, {
        doctype: uploadForm.doctype,
        document_name: uploadForm.document_name.trim(),
        file: uploadForm.file,
      });
      const docs = await fetchPatientDocuments(docPatient.id);
      setDocuments(docs || []);
      setUploadForm({ doctype: "clinical", document_name: "", file: null });
      setSnackbar({ open: true, message: "Document uploaded", severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: toErrorMessage(e, "Failed to upload document"), severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadChange = (event) => {
    const { name, value } = event.target;
    setUploadForm((prev) => ({ ...prev, [name]: value }));
  };

  const setUploadFile = (file) => {
    if (!file) return;
    setUploadForm((prev) => ({
      ...prev,
      file,
      document_name: prev.document_name || file.name,
    }));
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    setUploadFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    setUploadFile(file);
  };

  const loadPatientSchedules = useCallback(async (patientId) => {
    setScheduleCache((prev) => ({
      ...prev,
      [patientId]: { loading: true, items: prev[patientId]?.items || [], error: "" },
    }));
    try {
      const schedules = await fetchPatientSchedules(patientId);
      const items = mapPatientScheduleRows(schedules);
      setScheduleCache((prev) => ({
        ...prev,
        [patientId]: { loading: false, items, error: "" },
      }));
    } catch (e) {
      setScheduleCache((prev) => ({
        ...prev,
        [patientId]: {
          loading: false,
          items: [],
          error: toErrorMessage(e, "Failed to load schedules"),
        },
      }));
    }
  }, []);

  const openScheduleDialogForPatient = useCallback((patientId, schedule, sortedSchedules) => {
    setSchedulePatientId(patientId);
    setScheduleDialog(buildScheduleDialogState(schedule, sortedSchedules));
  }, []);

  const handleScheduleDateSave = async () => {
    if (!schedulePatientId || !scheduleDialog.scheduleId || !scheduleDialog.selectedDate) return;
    setScheduling(true);
    try {
      await updatePatientScheduleDate(
        schedulePatientId,
        scheduleDialog.scheduleId,
        scheduleDialog.selectedDate
      );
      setSnackbar({ open: true, message: "Schedule date updated", severity: "success" });
      setScheduleDialog(emptyScheduleDialogState);
      setSchedulePatientId(null);
      await loadPatientSchedules(schedulePatientId);
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to update schedule date"),
        severity: "error",
      });
    } finally {
      setScheduling(false);
    }
  };

  const toggleScheduleExpand = useCallback(
    (row, event) => {
      event?.stopPropagation?.();
      const patientId = row?.id;
      if (!patientId) return;
      if (String(expandedPatientId) === String(patientId)) {
        setExpandedPatientId(null);
        return;
      }
      setExpandedPatientId(patientId);
      const cached = scheduleCache[patientId];
      if (!cached?.items?.length && !cached?.loading) {
        loadPatientSchedules(patientId);
      }
    },
    [expandedPatientId, scheduleCache, loadPatientSchedules]
  );

  const displayRows = useMemo(() => {
    const next = [];
    (rows || []).forEach((row) => {
      next.push({ ...row, __rowType: "data" });
      if (expandedPatientId != null && String(expandedPatientId) === String(row.id)) {
        const label = [row.first_name, row.last_name].filter(Boolean).join(" ").trim() || row.pid || "";
        next.push({
          id: `schedule-detail-${row.id}`,
          __rowType: "scheduleDetail",
          __parentId: row.id,
          __patientLabel: label,
        });
      }
    });
    return next;
  }, [rows, expandedPatientId]);

  const wrapDataColumn = (column) => ({
    ...column,
    colSpan: (_value, row) => (row?.__rowType === "scheduleDetail" ? 0 : 1),
    renderCell: column.renderCell
      ? (params) => {
          if (params.row?.__rowType === "scheduleDetail") return null;
          return column.renderCell(params);
        }
      : column.renderCell,
  });

  const handleDeleteDocument = async (docId) => {
    if (!docPatient?.id) return;
    try {
      await deletePatientDocument(docPatient.id, docId);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
      setSnackbar({ open: true, message: "Document deleted", severity: "success" });
    } catch (e) {
      setSnackbar({ open: true, message: toErrorMessage(e, "Failed to delete document"), severity: "error" });
    }
  };

  const columns = useMemo(
    () => [
      {
        field: "__expand",
        headerName: "",
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        colSpan: (_value, row) =>
          row?.__rowType === "scheduleDetail" ? DATA_GRID_COLUMN_COUNT : 1,
        renderCell: (params) => {
          if (params.row?.__rowType === "scheduleDetail") {
            const cache = scheduleCache[params.row.__parentId] || {};
            return (
              <PatientSchedulePanel
                schedules={cache.items || []}
                loading={Boolean(cache.loading)}
                error={cache.error || ""}
                patientLabel={params.row.__patientLabel}
                onScheduleClick={(schedule, sortedSchedules) =>
                  openScheduleDialogForPatient(params.row.__parentId, schedule, sortedSchedules)
                }
              />
            );
          }
          const isExpanded = String(expandedPatientId) === String(params.row?.id);
          return (
            <IconButton
              size="small"
              aria-label={isExpanded ? "Collapse schedules" : "Expand schedules"}
              onClick={(event) => toggleScheduleExpand(params.row, event)}
            >
              {isExpanded ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
            </IconButton>
          );
        },
      },
      wrapDataColumn({
        field: "pid",
        headerName: "PID",
        minWidth: 120,
        flex: 0.9,
        renderCell: (params) => (
          <DatagridNavLink to={params.row.id ? `/subject-management/edit/${params.row.id}` : ""}>
            {params.value}
          </DatagridNavLink>
        ),
      }),
      wrapDataColumn({ field: "first_name", headerName: "First Name", minWidth: 140, flex: 1 }),
      wrapDataColumn({ field: "last_name", headerName: "Last Name", minWidth: 140, flex: 1 }),
      wrapDataColumn({ field: "email", headerName: "Email", minWidth: 180, flex: 1.2 }),
      wrapDataColumn({ field: "phone", headerName: "Phone", minWidth: 130, flex: 0.9 }),
      wrapDataColumn({
        field: "client_name",
        headerName: "Client",
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.client_id ? `/client-management/edit/${params.row.client_id}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      }),
      wrapDataColumn({
        field: "project_name",
        headerName: "Project",
        minWidth: 140,
        flex: 1,
        renderCell: (params) => (
          <DatagridNavLink
            to={params.row.project_id ? `/project-details/${params.row.project_id}` : ""}
          >
            {params.value}
          </DatagridNavLink>
        ),
      }),
      wrapDataColumn({
        field: "progress_percent",
        headerName: "Schedules Delivery",
        minWidth: 240,
        flex: 1.2,
        sortable: false,
        renderCell: (params) => {
          const value = Number(params.row?.progress_percent || 0);
          const completed = Number(params.row?.completed_visits || 0);
          const total = Number(params.row?.total_visits || 0);
          const derivedStatus = String(params.row?.status || "active").toUpperCase();
          return (
            <Box sx={{ width: "100%", py: 0.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.3 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>
                  {derivedStatus}
                </Typography>
                <Typography variant="caption">
                  {completed}/{total} ({value}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.max(0, Math.min(100, value))}
                color={derivedStatus === "COMPLETED" ? "success" : "primary"}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  ...(derivedStatus === "INACTIVE"
                    ? { "& .MuiLinearProgress-bar": { backgroundColor: "#9e9e9e" } }
                    : {}),
                }}
              />
            </Box>
          );
        },
      }),
      wrapDataColumn({
        field: "survey_completion",
        headerName: "Survey Completion",
        minWidth: 140,
        flex: 0.7,
        sortable: false,
        renderCell: (params) => {
          const value = Number(params.row?.forms_percent || 0);
          const completed = Number(params.row?.form_schedules_completed || 0);
          const total = Number(params.row?.form_schedules_total || 0);
          const patientStatusUpper = String(params.row?.status || "active").toUpperCase();
          const isComplete = total > 0 && completed >= total;
          const displayValue =
            patientStatusUpper === "INACTIVE" ? "No" : isComplete ? "Yes" : "No";
          const tooltipParts = [
            `${completed} of ${total} survey(s) completed (${value}%)`,
            patientStatusUpper === "INACTIVE" ? "Subject is inactive" : null,
            total === 0 ? "No surveys configured yet" : null,
          ].filter(Boolean);

          return (
            <Tooltip title={tooltipParts.join(" · ")} arrow placement="top">
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: displayValue === "Yes" ? "success.main" : "text.secondary",
                  cursor: "default",
                }}
              >
                {displayValue}
              </Typography>
            </Tooltip>
          );
        },
      }),
      wrapDataColumn({
        field: "enrollment_date",
        headerName: "Enrollment Date",
        minWidth: 150,
        flex: 0.9,
        renderCell: (params) => formatDate(params.value),
      }),
      wrapDataColumn({
        field: "created_at",
        headerName: "Created At",
        minWidth: 150,
        flex: 0.9,
        renderCell: (params) => formatDate(params.value),
      }),
      wrapDataColumn({
        field: "actions",
        headerName: "Actions",
        minWidth: 150,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Edit">
              <span>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={!canManage}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(`/subject-management/edit/${params.row.id}`);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Documents">
              <span>
                <IconButton size="small" color="primary" disabled={!canManage} onClick={() => openDocumentDialog(params.row)}>
                  <UploadFileIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete">
              <span>
                <IconButton size="small" color="error" disabled={!canManage} onClick={() => setDeleteId(params.row.id)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      }),
    ],
    [canManage, navigate, expandedPatientId, scheduleCache, toggleScheduleExpand, openScheduleDialogForPatient]
  );

  return (
    <Box sx={{ bgcolor: "#dde7ecff", minHeight: "100vh" }}>
      <PageBanner title="Subject Management" />

      <PageContent width={{ xs: "100%", sm: "95%", md: "90%" }}>
        <Box sx={{ borderRadius: 1, p: { xs: 1.5, sm: 2 }, bgcolor: "#ffffff" }}>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <TextField
              size="small"
              placeholder="Search by PID, name, email, phone"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              autoComplete="off"
              inputProps={{ autoComplete: "new-patient-search" }}
              sx={{ width: { xs: "100%", sm: 320 } }}
            />
            <TextField
              size="small"
              select
              label="Client Filter"
              value={clientFilter}
              onChange={async (event) => {
                const cid = event.target.value;
                setClientFilter(cid);
                setProjectFilter("");
                await loadProjectsForClient(cid, false);
              }}
              sx={{ width: { xs: "100%", sm: 220 } }}
            >
              <MenuItem value="">All Clients</MenuItem>
              {(clients || []).map((c) => (
                <MenuItem key={c.cid} value={c.cid}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Project Filter"
              value={projectFilter}
              onChange={(event) => setProjectFilter(event.target.value)}
              disabled={!clientFilter}
              sx={{ width: { xs: "100%", sm: 220 } }}
            >
              <MenuItem value="">All Projects</MenuItem>
              {(projectsByClient || []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
            <DatePicker
              label="Enrollment From"
              value={enrollmentDateFrom ? dayjs(enrollmentDateFrom) : null}
              onChange={(value) => {
                setEnrollmentDateFrom(value && value.isValid() ? value.format("YYYY-MM-DD") : "");
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: "100%", sm: 180 } },
                },
              }}
            />
            <DatePicker
              label="Enrollment To"
              value={enrollmentDateTo ? dayjs(enrollmentDateTo) : null}
              onChange={(value) => {
                setEnrollmentDateTo(value && value.isValid() ? value.format("YYYY-MM-DD") : "");
                setPaginationModel((prev) => ({ ...prev, page: 0 }));
              }}
              slotProps={{
                textField: {
                  size: "small",
                  sx: { width: { xs: "100%", sm: 180 } },
                },
              }}
            />
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <DataGrid
            rows={displayRows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            sortingMode="server"
            rowCount={rowCount}
            pageSizeOptions={[10, 20, 50]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            autoHeight
            disableRowSelectionOnClick
            isRowSelectable={(params) => params.row?.__rowType !== "scheduleDetail"}
            getRowHeight={(params) =>
              params.model?.__rowType === "scheduleDetail" ? "auto" : undefined
            }
            getRowClassName={(params) => {
              if (params.row?.__rowType === "scheduleDetail") return "schedule-detail-row";
              return params.indexRelativeToCurrentPage % 2 === 0 ? "row-even" : "row-odd";
            }}
            sx={{
              "& .row-even": {
                backgroundColor: "#ffffff",
              },
              "& .row-odd": {
                backgroundColor: "#f5f5f5",
              },
              "& .schedule-detail-row": {
                backgroundColor: "#eef4f9",
                "&:hover": {
                  backgroundColor: "#eef4f9",
                },
              },
              "& .schedule-detail-row .MuiDataGrid-cell": {
                py: 0,
                alignItems: "flex-start",
                overflow: "visible",
                whiteSpace: "normal",
                lineHeight: "normal",
                maxWidth: "none !important",
              },
              "& .schedule-detail-row .MuiDataGrid-cell > div": {
                width: "100%",
                maxWidth: "100%",
              },
            }}
          />
        </Box>
      </PageContent>

      <Dialog
        open={createOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setCreateOpen(false);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{editId ? "Edit Subject" : "Add Subject"}</DialogTitle>
        <DialogContent sx={{ bgcolor: "#f5f8fb" }}>
          <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ p: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="PID (Auto)"
                    value={form.pid}
                    disabled
                    autoComplete="off"
                    inputProps={{
                      autoComplete: "new-patient-pid",
                      name: "pid_display",
                      readOnly: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    required
                    label="Client"
                    value={form.client_id}
                    error={Boolean(fieldErrors.client_id)}
                    helperText={fieldErrors.client_id}
                    onChange={(event) => {
                      handleClientChange(event.target.value);
                      clearPatientFieldError(setFieldErrors, "client_id");
                    }}
                    sx={modalSelectSx}
                  >
                    {(clients || []).map((c) => (
                      <MenuItem key={c.cid} value={c.cid}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    required
                    label="Project"
                    value={form.project_id}
                    error={Boolean(fieldErrors.project_id)}
                    helperText={fieldErrors.project_id}
                    onChange={(event) => {
                      handleProjectChange(event.target.value);
                      clearPatientFieldError(setFieldErrors, "project_id");
                    }}
                    disabled={!form.client_id}
                    sx={modalSelectSx}
                  >
                    {(projectsByClient || []).map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="First Name"
                    value={form.first_name}
                    error={Boolean(fieldErrors.first_name)}
                    helperText={fieldErrors.first_name}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, first_name: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "first_name");
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Middle Name" value={form.middle_name} onChange={(e) => setForm((p) => ({ ...p, middle_name: e.target.value }))} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="Last Name"
                    value={form.last_name}
                    error={Boolean(fieldErrors.last_name)}
                    helperText={fieldErrors.last_name}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, last_name: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "last_name");
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    required
                    label="Gender"
                    value={form.gender}
                    error={Boolean(fieldErrors.gender)}
                    helperText={fieldErrors.gender}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, gender: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "gender");
                    }}
                    sx={modalSelectSx}
                  >
                    <MenuItem value="">Select</MenuItem>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Date of Birth"
                    format="MM/DD/YYYY"
                    value={form.date_of_birth ? dayjs(form.date_of_birth) : null}
                    onChange={(value) => {
                      setForm((p) => ({
                        ...p,
                        date_of_birth: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                      }));
                      clearPatientFieldError(setFieldErrors, "date_of_birth");
                    }}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(fieldErrors.date_of_birth),
                        helperText: fieldErrors.date_of_birth,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <DatePicker
                    label="Enrollment date"
                    format="MM/DD/YYYY"
                    value={form.enrollment_date ? dayjs(form.enrollment_date) : null}
                    onChange={(value) => {
                      setForm((p) => ({
                        ...p,
                        enrollment_date: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                      }));
                      clearPatientFieldError(setFieldErrors, "enrollment_date");
                    }}
                    disabled={Boolean(editId)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: !editId,
                        error: Boolean(fieldErrors.enrollment_date),
                        helperText:
                          fieldErrors.enrollment_date ||
                          (editId
                            ? "Enrollment date cannot be changed after the subject is created."
                            : "Required; first static visit is scheduled on this date."),
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField select label="Subject Status" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))} sx={modalSelectSx}>
                    <MenuItem value="active">Active (Auto)</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    type="email"
                    label="Email"
                    value={form.email}
                    disabled={Boolean(editId)}
                    error={Boolean(fieldErrors.email)}
                    helperText={
                      fieldErrors.email ||
                      (editId
                        ? "Cannot be edited after the subject is created."
                        : "Must be unique among subjects; can match a client manager email if no other subject uses it.")
                    }
                    onChange={(e) => {
                      setForm((p) => ({ ...p, email: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "email");
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    required
                    label="Country"
                    value={form.country}
                    error={Boolean(fieldErrors.country)}
                    helperText={fieldErrors.country}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, country: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "country");
                    }}
                    sx={modalSelectSx}
                  >
                    {COUNTRIES.map((country) => (
                      <MenuItem key={country.code} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Country Code" value={selectedCountry?.dialCode || ""} disabled />
                </Grid>
                <Grid item xs={12} md={9}>
                  <TextField
                    required
                    fullWidth
                    label="Mobile Number"
                    value={form.phone_local}
                    error={Boolean(fieldErrors.phone)}
                    helperText={fieldErrors.phone}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, phone_local: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "phone");
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ p: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
                Address
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    required
                    fullWidth
                    label="Address 1"
                    value={form.address1}
                    error={Boolean(fieldErrors.address1)}
                    helperText={fieldErrors.address1}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, address1: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "address1");
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Address 2"
                    value={form.address2}
                    onChange={(e) => setForm((p) => ({ ...p, address2: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="City"
                    value={form.city}
                    error={Boolean(fieldErrors.city)}
                    helperText={fieldErrors.city}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, city: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "city");
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="State"
                    value={form.state}
                    error={Boolean(fieldErrors.state)}
                    helperText={fieldErrors.state}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, state: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "state");
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    required
                    fullWidth
                    label="Zipcode"
                    value={form.zipcode}
                    error={Boolean(fieldErrors.zipcode)}
                    helperText={fieldErrors.zipcode}
                    onChange={(e) => {
                      setForm((p) => ({ ...p, zipcode: e.target.value }));
                      clearPatientFieldError(setFieldErrors, "zipcode");
                    }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ bgcolor: "#fff", border: "1px solid #dde5ec", borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Schedule Timeline Preview
              </Typography>
              {(schedulePreview || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Select a project to preview schedule windows. Visit 1 uses the enrollment date (or today until you set it).
                </Typography>
              ) : (
                <Box sx={{ overflowX: "auto", pb: 1 }}>
                  <Timeline
                    sx={{
                      m: 0,
                      p: 0,
                      minWidth: "max-content",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "flex-start",
                      "& .MuiTimelineItem-root": {
                        minHeight: "auto",
                        flex: "0 0 320px",
                        px: 0,
                        alignItems: "stretch",
                        "&:before": { display: "none" },
                      },
                      "& .MuiTimelineSeparator-root": {
                        width: "100%",
                        flexDirection: "row",
                        alignItems: "center",
                        minHeight: "auto",
                        mb: 1,
                      },
                      "& .MuiTimelineConnector-root": {
                        flexGrow: 1,
                        width: "auto",
                        height: 2,
                        bgcolor: "#90a4ae",
                      },
                      "& .MuiTimelineContent-root": { py: 0, px: 0 },
                    }}
                  >
                    {(schedulePreview || []).map((s, index) => {
                      const isDynamic = String(s.type || "").toLowerCase() === "dynamic";
                      return (
                        <TimelineItem key={s.id}>
                          <TimelineSeparator>
                            {index > 0 ? <TimelineConnector /> : null}
                            <TimelineDot color={isDynamic ? "secondary" : "primary"}>
                              {isDynamic ? <AutorenewIcon fontSize="small" /> : <EventIcon fontSize="small" />}
                            </TimelineDot>
                            {index < schedulePreview.length - 1 ? <TimelineConnector /> : null}
                          </TimelineSeparator>
                          <TimelineContent>
                            <Box sx={{ border: "1px solid #dce5ed", borderRadius: 2, p: 1.5, bgcolor: "#fff" }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                Schedule {s.schedule} ({String(s.type || "").toUpperCase()})
                              </Typography>
                              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
                                Tolerance: {s.tolerance}
                              </Typography>
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#1976d2" }}>
                                  <LockOpenIcon sx={{ fontSize: 18 }} />
                                  <Typography variant="body2">Window Open: {s.window_open == null ? "N/A" : formatDate(s.window_open)}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#2e7d32" }}>
                                  <GpsFixedIcon sx={{ fontSize: 18 }} />
                                  <Typography variant="body2">Optimal Date: {s.optimal_date == null ? "N/A" : formatDate(s.optimal_date)}</Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, color: "#ed6c02" }}>
                                  <LockIcon sx={{ fontSize: 18 }} />
                                  <Typography variant="body2">Window Close: {s.window_close == null ? "N/A" : formatDate(s.window_close)}</Typography>
                                </Box>
                              </Box>
                            </Box>
                          </TimelineContent>
                        </TimelineItem>
                      );
                    })}
                  </Timeline>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? "Saving..." : editId ? "Update Subject" : "Create Subject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={docDialogOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setDocDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          Add Subject Document
          <IconButton
            aria-label="close"
            onClick={() => setDocDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Subject: {docPatient?.pid || ""}
          </Typography>
          <Box sx={{ pt: 1.5, display: "grid", gap: 2 }}>
            <TextField
              select
              name="doctype"
              label="Document Type"
              value={uploadForm.doctype}
              onChange={handleUploadChange}
              fullWidth
            >
              {doctypeOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              name="document_name"
              label="Document Name"
              value={uploadForm.document_name}
              onChange={handleUploadChange}
              fullWidth
            />

            <Box
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "1px dashed #9aa4b2",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragActive ? "#eef4ff" : "transparent",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Drag and drop file here, or click to select
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                {uploadForm.file ? `Selected: ${uploadForm.file.name}` : "No file selected"}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={handleFileInput}
              />
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            {(documents || []).map((doc) => (
              <Box key={doc.id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.8 }}>
                <Typography variant="body2">
                  {doc.document_name} ({doc.doctype})
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    size="small"
                    onClick={async () => {
                      const token = localStorage.getItem("authToken");
                      const response = await fetch(getPatientDocumentDownloadUrl(docPatient.id, doc.id), {
                        method: "GET",
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!response.ok) {
                        setSnackbar({ open: true, message: "Failed to download document", severity: "error" });
                        return;
                      }
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = doc.filename || "document";
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    }}
                  >
                    Download
                  </Button>
                  <Button size="small" color="error" onClick={() => handleDeleteDocument(doc.id)}>
                    Delete
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDocDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUploadDocument} variant="contained" disabled={uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <ScheduleDateDialog
        open={scheduleDialog.open}
        onClose={() => {
          setScheduleDialog(emptyScheduleDialogState);
          setSchedulePatientId(null);
        }}
        onSelectedDateChange={(selectedDate) =>
          setScheduleDialog((prev) => ({ ...prev, selectedDate }))
        }
        onSave={handleScheduleDateSave}
        scheduling={scheduling}
        scheduleDialog={scheduleDialog}
      />

      <AppConfirmDialog
        open={Boolean(deleteId)}
        title="Delete Subject"
        content="Are you sure you want to delete this subject?"
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteId(null)}
        onConfirm={handleDelete}
      />

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />

      <Tooltip title={<Typography sx={{ fontSize: "1rem", fontWeight: "bold" }}>Add Subject</Typography>}>
        <Fab
          color="primary"
          aria-label="add-subject"
          onClick={() => openCreate(null)}
          disabled={!canManage}
          sx={{
            position: "fixed",
            bottom: { xs: 24, sm: 32 },
            right: { xs: 24, sm: 32 },
            zIndex: 1000,
          }}
        >
          <AddIcon />
        </Fab>
      </Tooltip>
    </Box>
  );
};

export default PatientList;
